import {
	Alert,
	Box,
	Chip,
	Dialog,
	DialogContent,
	DialogTitle,
	Grid,
	IconButton,
	Paper,
	Stack,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TablePagination,
	TableRow,
	Typography,
} from "@mui/material";
import { format } from "date-fns";
import CloseIcon from "@mui/icons-material/Close";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import { ptBR } from "date-fns/locale";
import { useEffect, useState } from "react";
import { ToastContent } from "../../../shared/components";
import { LoadingComp } from "../../../shared/components/loading/Loading";
import { Environment } from "../../../shared/environment";
import { useToast } from "../../../shared/hooks";
import { IProvisionListBySinister, ProvisionService } from "../../../shared/services/api/provisoes/ProvisionService";
import { globalBackground, globalColorText, GlobalCoolorIconSinister, globalTranslate } from "../../../shared/utils";
import { TableDetailBordero } from "./TableDetailBordero";
import type { BorderoMenuModalType } from "./Menus/BorderoRowMenu";
import { BorderoRowMenu } from "./Menus/BorderoRowMenu";
import { ModalSuspendBordero } from "./Modals/ModalSuspendBordero";
import { ModalLiftSuspensionBordero } from "./Modals/ModalLiftSuspensionBordero";
import { ModalCancelBorderoSinister } from "./Modals/ModalCancelBorderoSinister";
import { ModalEditBorderoBankData } from "./Modals/ModalEditBorderoBankData";

const normalizeStatusForSoftChip = (status: string) => {
	if (status === "approved" || status === "accepted") return "issued";
	if (status === "provision") return "provisioned";
	if (status === "analysis") return "pending";
	return status;
};

// Status "suspenso" (item 6/14) e "agendado" (item 16) tratados à parte — não usamos
// globalTranslate/globalBackground pra eles porque essas funções são compartilhadas com vários
// outros módulos que já usam a chave "suspended" com outro sentido; aqui isolamos pra não colidir.
const isSuspendedStatus = (status: string) => status === "suspended_sinistro" || status === "suspended_financeiro";
// "Agendado" (item 16) — tag/marcador de controle do Financeiro sobre o lançamento no banco,
// refletida aqui no Sinistro. Só o Financeiro consegue setar (módulo próprio, ainda não
// implementado — ver GAPS_BORDERO_PARA_BACKEND.md) e prevalece: enquanto ativa, o setor de
// Sinistro não consegue suspender/cancelar esse borderô por aqui (ver BorderoRowMenu).
const isScheduledStatus = (status: string) => status === "scheduled_financeiro";

const specialStatusLabel: Record<string, string> = {
	suspended_sinistro: "Suspenso Sinistro",
	suspended_financeiro: "Suspenso Financeiro",
	scheduled_financeiro: "Agendado",
};

const suspendedColor = { bg: "#fdecea", text: "#c0392b" };
const scheduledColor = { bg: "#eef6ff", text: "#1565c0" };

interface Props {
	SinisterId: string;
}

export function TableBordero({ SinisterId }: Props) {
	const [rows, setRowsProvision] = useState<IProvisionListBySinister[]>([]);
	const [totalCount, setTotalCount] = useState(0);
	const [isLoading, setIsLoading] = useState(false);
	const [page, setPage] = useState(0);
	const [rowsPerPage, setRowsPerPage] = useState(10);
	const [openModalDetail, setOpenModalDetail] = useState(false);
	const [selectedBorderoId, setSelectedBorderoId] = useState<string | null>(null);

	const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
	const [selectedRow, setSelectedRow] = useState<IProvisionListBySinister | null>(null);
	const [modalType, setModalType] = useState<BorderoMenuModalType | null>(null);

	const toastHandler = useToast();

	const handleFetchBorderos = async () => {
		try {
			setIsLoading(true);
			const result = await ProvisionService.getAllBorderoById(SinisterId);

			if (result instanceof Error) {
				toastHandler.present({
					type: "error",
					messageOrContent: (
						<ToastContent title="Erro ao carregar borderôs" content={result.message || "Não foi possível buscar os dados do borderô."} />
					),
				});
				return;
			}

			setRowsProvision(result.data);
			setTotalCount(result.totalCount);
		} catch (error: any) {
			toastHandler.present({
				type: "error",
				messageOrContent: <ToastContent title="Erro inesperado" content={error?.message || "Algo deu errado ao carregar os dados."} />,
			});
		} finally {
			setIsLoading(false);
		}
	};

	useEffect(() => {
		handleFetchBorderos();
	}, []);

	useEffect(() => {
		setPage(0);
	}, [SinisterId]);

	const pagedRows = rows.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

	const handleOpenDetailModal = (id: string) => {
		setSelectedBorderoId(id);
		setOpenModalDetail(true);
	};

	const handleCloseDetailModal = () => {
		setOpenModalDetail(false);
		setSelectedBorderoId(null);
	};

	const handleMenuOpen = (event: React.MouseEvent<HTMLElement>, row: IProvisionListBySinister) => {
		setAnchorEl(event.currentTarget);
		setSelectedRow(row);
	};

	const handleMenuClose = () => setAnchorEl(null);

	const handleCloseActionModal = () => {
		setSelectedRow(null);
		setModalType(null);
	};

	const handleOpenActionModal = (type: BorderoMenuModalType, row: IProvisionListBySinister) => {
		setSelectedRow(row);
		if (type === "details") {
			handleOpenDetailModal(row.id);
			return;
		}
		setModalType(type);
	};

	const handleActionSuccess = () => {
		handleCloseActionModal();
		handleFetchBorderos();
	};

	const detailRow = rows.find((row) => row.id === selectedBorderoId);

	return (
		<>
			<LoadingComp isopen={isLoading} />
			<Grid container item direction="row" spacing={2}>
				<Grid item xs={12}>
					<TableContainer component={Paper} elevation={0} variant="elevation" sx={{ borderRadius: 0, overflow: "hidden", boxShadow: "none" }}>
						<Table size="small" stickyHeader aria-label="Tabela de borderôs">
							<TableHead>
								<TableRow sx={{ backgroundColor: "grey.100" }}>
									<TableCell sx={{ width: "1%" }}>
										<Typography variant="button">#</Typography>
									</TableCell>
									<TableCell align="center">
										<Typography variant="button">Criação</Typography>
									</TableCell>
									<TableCell align="center">
										<Typography variant="button">Limite</Typography>
									</TableCell>
									<TableCell align="center">
										<Typography variant="button">Provisão</Typography>
									</TableCell>
									<TableCell align="right">
										<Typography variant="button">Valor</Typography>
									</TableCell>
									<TableCell>
										<Typography variant="button">Status</Typography>
									</TableCell>
									<TableCell align="right" sx={{ p: 0, width: "1%" }}>
										<Typography variant="button">Ações</Typography>
									</TableCell>
								</TableRow>
							</TableHead>

							<TableBody>
								{pagedRows.map((row) => (
									<TableRow
										key={row.id}
										hover
										sx={{
											"& .MuiTableCell-root": {
												py: 0.75,
											},
										}}
									>
										<TableCell>
											<Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.2, fontWeight: 600 }}>
												{row.sequencial}
											</Typography>
										</TableCell>
										<TableCell align="center">
											<Stack spacing={0.25}>
												<Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.2 }}>
													{row.createdAt ? format(new Date(row.createdAt), "P", { locale: ptBR }) : "-"}
												</Typography>
												<Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.2 }}>
													{row.createdAt ? format(new Date(row.createdAt), "HH:mm:ss") : ""}
												</Typography>
											</Stack>
										</TableCell>
										<TableCell align="center">
											<Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.2 }}>
												{row.dueDate ? format(new Date(row.dueDate), "P", { locale: ptBR }) : "-"}
											</Typography>
										</TableCell>
										<TableCell align="center">
											<Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.2 }}>
												{row.provisionedAt ? format(new Date(row.provisionedAt), "P", { locale: ptBR }) : "-"}
											</Typography>
										</TableCell>
										<TableCell align="right">
											<Typography variant="body2" fontWeight={500} color="text.secondary" sx={{ lineHeight: 1.2 }}>
												{row.value != null
													? parseFloat(`${row.value}`).toLocaleString("pt-br", { style: "currency", currency: "BRL" })
													: "-"}
											</Typography>
										</TableCell>
										<TableCell>
											<Box display="flex" flexDirection="column" gap={0.5}>
												<Chip
													size="medium"
													label={
														isSuspendedStatus(row.status) || isScheduledStatus(row.status)
															? specialStatusLabel[row.status]
															: globalTranslate(row.status)
													}
													sx={{
														color: isSuspendedStatus(row.status)
															? suspendedColor.text
															: isScheduledStatus(row.status)
															? scheduledColor.text
															: globalColorText(normalizeStatusForSoftChip(row.status)),
														backgroundColor: isSuspendedStatus(row.status)
															? suspendedColor.bg
															: isScheduledStatus(row.status)
															? scheduledColor.bg
															: globalBackground(normalizeStatusForSoftChip(row.status)),
														fontWeight: 500,
														border: "0.5px solid #f1f1f1",
														width: "fit-content",
													}}
												/>
												{row.paymentAt && (
													<Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.2 }}>
														{format(new Date(row.paymentAt), "P", { locale: ptBR })}
													</Typography>
												)}
												{isScheduledStatus(row.status) && row.scheduledDate && (
													<Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.2 }}>
														p/ {format(new Date(row.scheduledDate), "P", { locale: ptBR })}
													</Typography>
												)}
											</Box>
										</TableCell>
										<TableCell align="right" sx={{ p: 0, width: "1%" }}>
											<IconButton size="small" onClick={(e) => handleMenuOpen(e, row)}>
												<MoreVertIcon sx={{ color: GlobalCoolorIconSinister(row.status) }} fontSize="small" />
											</IconButton>
										</TableCell>
									</TableRow>
								))}
							</TableBody>
						</Table>

						{totalCount === 0 && !isLoading && <caption>{Environment.LISTAGEM_VAZIA}</caption>}

						{/* Paginação (padrão financeiro) */}
						{totalCount > 0 && (
							<Box display="flex" justifyContent="flex-end" alignItems="center" mt={2} pr={2}>
								<TablePagination
									component="div"
									count={totalCount}
									page={page}
									rowsPerPage={rowsPerPage}
									onPageChange={(_, newPage) => setPage(newPage)}
									onRowsPerPageChange={(e) => {
										setRowsPerPage(parseInt(e.target.value, 10));
										setPage(0);
									}}
									rowsPerPageOptions={[10, 25, 50]}
									labelRowsPerPage="Linhas por página:"
									labelDisplayedRows={({ from, to, count }) => `${from}-${to} de ${count} registros`}
								/>
							</Box>
						)}
					</TableContainer>
				</Grid>
			</Grid>

			<BorderoRowMenu
				anchorEl={anchorEl}
				open={Boolean(anchorEl)}
				onClose={handleMenuClose}
				selectedRow={selectedRow}
				onOpenModal={handleOpenActionModal}
			/>

			{/* Modal com detalhe do borderô */}
			<Dialog open={openModalDetail} onClose={handleCloseDetailModal} fullWidth maxWidth="md">
				<DialogTitle>
					<Grid container justifyContent="space-between" alignItems="center">
						<Typography variant="h5" sx={{ color: "primary.main", fontWeight: 300 }}>
							Detalhes do Borderô {detailRow?.sequencial}
						</Typography>
						<IconButton onClick={handleCloseDetailModal} size="small">
							<CloseIcon />
						</IconButton>
					</Grid>
				</DialogTitle>
				<DialogContent dividers>
					{detailRow && isSuspendedStatus(detailRow.status) && (
						<Alert severity="warning" sx={{ mb: 2 }}>
							<strong>{specialStatusLabel[detailRow.status]}</strong>
							{detailRow.suspendedReason ? ` — ${detailRow.suspendedReason}` : ""}
							{detailRow.suspendedAt ? ` (${format(new Date(detailRow.suspendedAt), "dd/MM/yyyy")})` : ""}
						</Alert>
					)}
					{detailRow && isScheduledStatus(detailRow.status) && (
						<Alert severity="info" sx={{ mb: 2 }}>
							<strong>Agendado pelo Financeiro</strong> — lançamento no banco programado
							{detailRow.scheduledDate ? ` para ${format(new Date(detailRow.scheduledDate), "dd/MM/yyyy")}` : ""}.
						</Alert>
					)}
					{selectedBorderoId && <TableDetailBordero BorderoId={selectedBorderoId} disableFetch={false} />}
				</DialogContent>
			</Dialog>

			{selectedRow && modalType === "suspend" && (
				<ModalSuspendBordero open onClose={handleCloseActionModal} onSuccess={handleActionSuccess} borderoId={selectedRow.id} />
			)}

			{selectedRow && modalType === "liftSuspension" && (
				<ModalLiftSuspensionBordero open onClose={handleCloseActionModal} onSuccess={handleActionSuccess} borderoId={selectedRow.id} />
			)}

			{selectedRow && modalType === "cancel" && (
				<ModalCancelBorderoSinister open onClose={handleCloseActionModal} onSuccess={handleActionSuccess} borderoId={selectedRow.id} />
			)}

			{modalType === "editBankData" && (
				<ModalEditBorderoBankData open={Boolean(selectedRow)} row={selectedRow} onClose={handleCloseActionModal} onSuccess={handleActionSuccess} />
			)}
		</>
	);
}
